import { logger } from '@/utils/logger/logger';

const TAG = 'Decryptor:';

/**
 * Decrypts a value encrypted with AES-256-CBC
 * Compatible with backend Encryptor class that uses:
 * - AES-256-CBC algorithm
 * - SHA-256 hashed key
 * - IV prepended to ciphertext (first 16 bytes)
 * - Base64 encoded output
 */
export async function decrypt(encryptedValue: string, key: string): Promise<string> {
  try {
    // 1. Base64 decode the encrypted value
    const encryptedBuffer = Uint8Array.from(atob(encryptedValue), c => c.charCodeAt(0));

    // 2. Extract IV (first 16 bytes) and encrypted data (rest)
    const iv = encryptedBuffer.slice(0, 16);
    const encryptedData = encryptedBuffer.slice(16);

    // 3. SHA256 hash the key
    const encoder = new TextEncoder();
    const keyData = encoder.encode(key);
    const hashBuffer = await crypto.subtle.digest('SHA-256', keyData);

    // 4. Import key for AES-256-CBC
    const cryptoKey = await crypto.subtle.importKey('raw', hashBuffer, { name: 'AES-CBC' }, false, [
      'decrypt',
    ]);

    // 5. Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      encryptedData
    );

    // 6. Decode to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    logger.error(TAG, 'Decryption failed', error);
    throw new Error('Failed to decrypt value');
  }
}

/**
 * Decrypts and parses a JSON value
 * Useful for decrypting arrays or objects that were JSON stringifies before encryption
 */
export async function decryptJson<T>(encryptedValue: string, key: string): Promise<T> {
  const decrypted = await decrypt(encryptedValue, key);
  return JSON.parse(decrypted) as T;
}
